"use client";

import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { Menu, X, ChevronDown, Sparkles, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { TrackedCTA } from "@/components/analytics/TrackedCTA";
import { trackEvent } from "@/lib/marketing/events";
import { useUserSession } from "@/components/providers/UserSessionProvider";

const navItems = [
  {
    labelKey: "nav.reports.label",
    href: "/matriz-del-destino",
    children: [
      { labelKey: "nav.reports.matrix.label",         href: "/matriz-del-destino",                      descKey: "nav.reports.matrix.desc" },
      { labelKey: "nav.reports.arcana.label",         href: "/matriz-del-destino/arcano",         descKey: "nav.reports.arcana.desc" },
      { labelKey: "nav.reports.natal.label",          href: "/carta-natal",       descKey: "nav.reports.natal.desc" },
      { labelKey: "nav.reports.personalPeriod.label", href: "/periodo-personal",              descKey: "nav.reports.personalPeriod.desc" },
      { labelKey: "nav.reports.money.label",          href: "/dinero-y-carrera",                        descKey: "nav.reports.money.desc" },
      { labelKey: "nav.reports.relocation.label",     href: "/mudanza-por-fecha-de-nacimiento",   descKey: "nav.reports.relocation.desc" },
    ],
  },
  {
    labelKey: "nav.people.label",
    href: "/compatibilidad",
    children: [
      { labelKey: "nav.people.compatibility.label", href: "/compatibilidad",    descKey: "nav.people.compatibility.desc" },
      { labelKey: "nav.people.children.label",      href: "/matriz-del-destino-infantil", descKey: "nav.people.children.desc" },
    ],
  },
  {
    labelKey: "nav.forecasts.label",
    href: "/pronostico-anual",
    children: [
      { labelKey: "nav.forecasts.personalDay.label",     href: "/dia-personal",  descKey: "nav.forecasts.personalDay.desc" },
      { labelKey: "nav.forecasts.personalYear.label",    href: "/anio-personal", descKey: "nav.forecasts.personalYear.desc" },
      { labelKey: "nav.forecasts.yearForecast.label",    href: "/pronostico-anual",      descKey: "nav.forecasts.yearForecast.desc" },
      { labelKey: "nav.forecasts.energyCalendar.label",  href: "/calendar",      descKey: "nav.forecasts.energyCalendar.desc" },
    ],
  },
  {
    labelKey: "nav.astrology.label",
    href: "/horoscopo",
    children: [
      { labelKey: "nav.astrology.horoscopeToday.label",       href: "/horoscopo-de-hoy",        descKey: "nav.astrology.horoscopeToday.desc" },
      { labelKey: "nav.astrology.zodiac.label",               href: "/signos-del-zodiaco",                      descKey: "nav.astrology.zodiac.desc" },
      { labelKey: "nav.astrology.zodiacCompatibility.label",  href: "/compatibilidad-de-signos", descKey: "nav.astrology.zodiacCompatibility.desc" },
      { labelKey: "nav.astrology.ascendant.label",            href: "/ascendente",                   descKey: "nav.astrology.ascendant.desc" },
      { labelKey: "nav.astrology.mercuryRetrograde.label",    href: "/mercurio-retrogrado",         descKey: "nav.astrology.mercuryRetrograde.desc" },
      { labelKey: "nav.astrology.lunarCalendar.label",        href: "/calendario-lunar",              descKey: "nav.astrology.lunarCalendar.desc" },
      // «Календарь стрижек 2026» и «Магнитные бури» вынесены в Footer-only
      // 2026-05-27: узкие SEO-страницы, перегружали dropdown. Footer их сохраняет.
    ],
  },
  {
    labelKey: "nav.tarot.label",
    href: "/tarot",
    children: [
      { labelKey: "nav.tarot.tarot.label",     href: "/tarot",           descKey: "nav.tarot.tarot.desc" },
      { labelKey: "nav.tarot.yesNo.label",     href: "/tarot-si-o-no", descKey: "nav.tarot.yesNo.desc" },
      { labelKey: "nav.tarot.dreamDict.label", href: "/diccionario-de-suenos",         descKey: "nav.tarot.dreamDict.desc" },
    ],
  },
  {
    labelKey: "nav.numerology.label",
    href: "/numerologia",
    children: [
      { labelKey: "nav.numerology.angelNumbers.label",       href: "/numeros-angelicales",                       descKey: "nav.numerology.angelNumbers.desc" },
      { labelKey: "nav.numerology.numberCompatibility.label", href: "/compatibilidad-de-numeros",   descKey: "nav.numerology.numberCompatibility.desc" },
      { labelKey: "nav.numerology.pythagoras.label",         href: "/cuadrado-de-pitagoras",                       descKey: "nav.numerology.pythagoras.desc" },
      { labelKey: "nav.numerology.lifePath.label",           href: "/numero-del-camino-de-vida",                descKey: "nav.numerology.lifePath.desc" },
      { labelKey: "nav.numerology.nameNumerology.label",     href: "/numero-del-nombre",                     descKey: "nav.numerology.nameNumerology.desc" },
      { labelKey: "nav.numerology.numerology.label",         href: "/numerologia",                           descKey: "nav.numerology.numerology.desc" },
      { labelKey: "nav.numerology.biorhythms.label",         href: "/biorritmos",                               descKey: "nav.numerology.biorhythms.desc" },
    ],
  },
  // Тарифы — top-level без dropdown. Главная конверсионная страница, должна
  // быть доступна в один клик (раньше была спрятана только в footer).
  { labelKey: "nav.pricing.label", href: "/pricing", children: undefined },
];

interface HeaderProps {
  flags?: {
    tarotEnabled?: boolean;
    sonnikEnabled?: boolean;
    blogEnabled?: boolean;
  };
}

export function Header({ flags }: HeaderProps) {
  const t = useTranslations("header");
  const { isLoggedIn } = useUserSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pathname = usePathname();

  const openDropdown = (href: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(href);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 120);
  };

  // Filter nav items based on feature flags
  const activeNavItems = navItems
    .map((item) => {
      if (item.labelKey === "nav.tarot.label") {
        const children = item.children?.filter((child) => {
          if (child.href === "/tarot") return flags?.tarotEnabled !== false;
          if (child.href === "/diccionario-de-suenos") return flags?.sonnikEnabled !== false;
          return true;
        });
        if (!children?.length) return null;
        return { ...item, children };
      }
      return item;
    })
    .filter(Boolean) as typeof navItems;

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-stone-100"
          : "bg-transparent"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-violet-grad flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg leading-none">Z</span>
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-lg font-semibold text-stone-900 leading-tight">
                Zaruvia
              </span>
              <span className="text-[10px] tracking-widest text-stone-400 uppercase mt-0.5">
                {t("logo.tagline")}
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {activeNavItems.map((item) => (
              <div
                key={item.href}
                className="relative"
                onMouseEnter={() => item.children && openDropdown(item.href)}
                onMouseLeave={scheduleClose}
              >
                <Link
                  href={item.href}
                  onClick={() => trackEvent("header_nav_click", { label: t(item.labelKey), href: item.href })}
                  className={cn(
                    "flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    pathname === item.href
                      ? "text-violet-700 bg-violet-50"
                      : "text-stone-600 hover:text-stone-900 hover:bg-stone-50"
                  )}
                >
                  {t(item.labelKey)}
                  {item.children && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
                </Link>

                {item.children && activeDropdown === item.href && (
                  <div className="absolute top-full left-0 w-64 pt-2 animate-fade-in-up">
                  <div className="bg-white rounded-xl shadow-xl border border-stone-100 p-2">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        onClick={() => trackEvent("header_nav_submenu_click", { parent: t(item.labelKey), label: t(child.labelKey), href: child.href })}
                        className="flex flex-col gap-0.5 px-3 py-2.5 rounded-lg hover:bg-violet-50 transition-colors group"
                      >
                        <span className="text-sm font-medium text-stone-800 group-hover:text-violet-700">
                          {t(child.labelKey)}
                        </span>
                        <span className="text-xs text-stone-400">{t(child.descKey)}</span>
                      </Link>
                    ))}
                  </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Auth buttons */}
          <div className="hidden lg:flex items-center gap-2">
            {isLoggedIn ? (
              <div className="flex items-center gap-2">
                <TrackedCTA
                  href="/dashboard"
                  eventName="header_dashboard_click"
                  eventProps={{ location: "desktop" }}
                >
                  <Button variant="soft" size="sm">
                    <User className="w-4 h-4" />
                    {t("cta.dashboard")}
                  </Button>
                </TrackedCTA>
              </div>
            ) : (
              <>
                <TrackedCTA
                  href="/auth/login"
                  eventName="header_login_click"
                  eventProps={{ location: "desktop" }}
                >
                  <Button variant="ghost" size="sm">{t("cta.login")}</Button>
                </TrackedCTA>
                <TrackedCTA
                  href="/auth/register?src=header"
                  eventName="header_register_click"
                  eventProps={{ location: "desktop" }}
                >
                  <Button variant="primary" size="sm">{t("cta.registerFree")}</Button>
                </TrackedCTA>
              </>
            )}
          </div>

          {/* Mobile: auth button + burger */}
          <div className="lg:hidden flex items-center gap-1.5">
            {isLoggedIn ? (
              <Link
                href="/dashboard"
                onClick={() => trackEvent("header_dashboard_click", { location: "mobile_header" })}
              >
                <Button variant="soft" size="sm" className="min-h-[44px] px-2.5 text-sm gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  {t("cta.dashboard")}
                </Button>
              </Link>
            ) : (
              <>
                <Link
                  href="/auth/login"
                  onClick={() => trackEvent("header_login_click", { location: "mobile_header" })}
                >
                  <Button variant="ghost" size="sm" className="min-h-[44px] px-2 text-sm">
                    {t("cta.login")}
                  </Button>
                </Link>
                <Link
                  href="/auth/register?src=header_mobile"
                  onClick={() => trackEvent("header_register_click", { location: "mobile_header" })}
                >
                  <Button variant="primary" size="sm" className="min-h-[44px] px-2.5 text-sm">
                    {t("cta.registerShort")}
                  </Button>
                </Link>
              </>
            )}
            <button
              className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t("aria.menu")}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-stone-100 shadow-lg max-h-[calc(100vh-64px)] overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            {activeNavItems.map((item) => (
              <div key={item.href}>
                <Link
                  href={item.href}
                  className="flex items-center px-3 py-2.5 rounded-lg text-sm font-medium text-stone-700 hover:bg-violet-50 hover:text-violet-700 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {t(item.labelKey)}
                </Link>
                {item.children && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="flex px-3 py-2 rounded-lg text-sm text-stone-500 hover:bg-stone-50 hover:text-stone-700 transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        {t(child.labelKey)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 border-t border-stone-100 flex flex-col gap-2">
              {isLoggedIn ? (
                <Link
                  href="/dashboard"
                  onClick={() => { trackEvent("header_dashboard_click", { location: "mobile" }); setMobileOpen(false); }}
                >
                  <Button variant="primary" size="md" className="w-full">{t("cta.dashboardFull")}</Button>
                </Link>
              ) : (
                <>
                  <Link
                    href="/auth/login"
                    onClick={() => { trackEvent("header_login_click", { location: "mobile" }); setMobileOpen(false); }}
                  >
                    <Button variant="outline" size="md" className="w-full">{t("cta.login")}</Button>
                  </Link>
                  <Link
                    href="/auth/register?src=header"
                    onClick={() => { trackEvent("header_register_click", { location: "mobile" }); setMobileOpen(false); }}
                  >
                    <Button variant="primary" size="md" className="w-full">{t("cta.registerFree")}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
